import {useEffect, useState} from 'react';
import {ErrorNotice, Loading, PageHeading} from './components';
import {errorDetails, request, useHermes} from './lib/store';
import type {ErrorDetails} from './lib/types';
import {AccountIdentity} from './auth';

type RequestStatus = {requestId: string; expiresAt: string; status?: 'pending' | 'authorized'; path?: string};

/** Browser approval uses the existing signed-in account and its CSRF token. */
export function DesktopHandoffPage({mode, search}: {mode: 'authorize' | 'browser'; search: string}) {
  const {state, actions} = useHermes();
  const id = new URLSearchParams(search).get('request');
  const [status, setStatus] = useState<RequestStatus | null>(null);
  const [error, setError] = useState<ErrorDetails | null>(null);
  const [busy, setBusy] = useState(false);
  const [callback, setCallback] = useState<string | null>(null);
  const base = mode === 'authorize' ? '/auth/desktop/requests' : '/auth/desktop/browser-actions';
  useEffect(() => {
    const controller = new AbortController();
    setStatus(null); setError(null); setCallback(null);
    if (!id || !/^[a-f0-9-]{36}$/i.test(id)) {setError(errorDetails(new Error('The desktop request address is invalid.'))); return;}
    void request<RequestStatus>(`${base}/${id}`, {signal: controller.signal}).then(value => {
      if (value.requestId !== id) throw new Error('The desktop request identity did not match.');
      if (!controller.signal.aborted) setStatus(value);
    }).catch(value => {if (!controller.signal.aborted) setError(errorDetails(value));});
    return () => controller.abort();
  }, [id, base, state.session.data?.csrfToken]);

  async function approve() {
    if (!status || !state.session.data || busy) return;
    setBusy(true); setError(null);
    try {
      if (mode === 'authorize') {
        const result = await request<{callbackUrl: string}>(`${base}/${status.requestId}/approve`, {body: {}, csrf: state.session.data.csrfToken});
        const url = new URL(result.callbackUrl);
        if (url.protocol !== 'eightbots:' || url.hostname !== 'auth' || url.pathname !== '/complete' ||
          url.searchParams.get('request_id') !== status.requestId || url.username || url.password || url.hash || [...url.searchParams].length !== 1) {
          throw new Error('The desktop callback address is invalid.');
        }
        setCallback(url.href);
        window.location.assign(url.href);
      } else {
        const result = await request<{redirectUrl: string}>(`${base}/${status.requestId}/continue`, {body: {}, csrf: state.session.data.csrfToken});
        const url = new URL(result.redirectUrl);
        if (url.origin !== window.location.origin || url.protocol !== 'https:' || url.username || url.password) {
          throw new Error('The browser action returned an invalid address.');
        }
        window.location.assign(url.href);
      }
    } catch (value) {setError(errorDetails(value));}
    finally {setBusy(false);}
  }

  return <section className="account-gate">
    <PageHeading title={mode === 'authorize' ? 'Connect the desktop app' : 'Continue in your browser'}>
      {mode === 'authorize' ? 'Use this account in the EightBots desktop app.' : 'Continue with the account used in your desktop app.'}
    </PageHeading>
    <AccountIdentity state={state} actions={actions}/>
    <ErrorNotice error={error}/>
    <Loading active={!status && !error}/>
    {callback ? <><p>The desktop app can now sign in.</p><a className="button" href={callback}>Return to EightBots</a></>
      : status && <button type="button" className="button" disabled={busy} onClick={() => void approve()}>
        {busy ? 'Connecting…' : mode === 'authorize' ? 'Connect desktop app' : 'Continue'}
      </button>}
  </section>;
}
